// S7 Apps list: "Apps you work on" cards (name, your role, environments, widget on/off),
// and "Register an app" for people with app.register (M11's /apps/new and
// /apps/$appId/settings/$section). Only Editors and Admins use it, and they see every app (poc CT-008).
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { appsQuery } from '@/lib/queries';
import { canApp, canTool, requireStaffSession, staffRoleLabel, useSession } from '@/lib/session';

export const Route = createFileRoute('/apps/')({
  beforeLoad: ({ context, location }) => requireStaffSession(context.queryClient, location.href),
  component: AppsList,
});

function AppsList() {
  const session = useSession();
  const apps = useQuery(appsQuery);
  const canRegister = canTool(session, 'app.register');
  const register = canRegister ? (
    <Button asChild>
      <Link to="/apps/new">
        <Plus aria-hidden="true" />
        Register an app
      </Link>
    </Button>
  ) : null;
  return (
    <>
      <PageHeader title="Apps you work on" actions={register} />
      {apps.isPending ? (
        <LoadingState label="Loading your apps" />
      ) : apps.isError ? (
        <ErrorState error={apps.error} onRetry={() => void apps.refetch()} />
      ) : apps.data.length === 0 ? (
        <EmptyState title="No apps are registered yet" action={register}>
          {canRegister ? 'Register the first one.' : 'An Editor or Admin registers them.'}
        </EmptyState>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {apps.data.map((app) => {
            const canManage = canApp(session, app.id, 'app.manage_settings');
            return (
              <li key={app.id}>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>
                      {canManage ? (
                        <Link
                          to="/apps/$appId/settings/$section"
                          params={{ appId: app.id, section: 'general' }}
                          className="hover:underline"
                        >
                          {app.name}
                        </Link>
                      ) : (
                        app.name
                      )}
                    </CardTitle>
                    <p className="text-sm text-muted">Your role: {staffRoleLabel(session)}</p>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-3">
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="muted">
                        {app.environmentCount} {app.environmentCount === 1 ? 'environment' : 'environments'}
                      </Badge>
                      <Badge variant={app.widgetState === 'on' ? 'success' : 'muted'}>Widget {app.widgetState}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild variant="secondary" size="sm">
                        <Link to="/apps/$appId/queue" params={{ appId: app.id }}>
                          Queue
                        </Link>
                      </Button>
                      {canManage ? (
                        <Button asChild variant="secondary" size="sm">
                          <Link to="/apps/$appId/settings/$section" params={{ appId: app.id, section: 'general' }}>
                            Settings
                          </Link>
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
